import { useLiveQuery } from 'dexie-react-hooks'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import type { ReactNode } from 'react'
import { useClub, useEngineState } from '../club/ClubProvider.tsx'
import { useServiceDue } from '../data/meters.ts'
import { useAssets, type AssetRow } from '../data/fleet.ts'
import { useRpc } from '../data/people.ts'
import { OPEN_WORK_ORDER_STATES, useAvailability, useDefectViews, useWorkOrders, type WorkOrderRow } from '../data/safety.ts'
import { usePlan, type PlanJob } from '../data/plan.ts'
import { areaShort, areasSummary, machineLabel, timeShort, useNames } from '../data/planUi.ts'
import { useAreas, type AreaRow } from '../data/geo.ts'
import { noticesFor, useNotices, type NoticeRow } from '../data/courseStatus.ts'
import { useIncidentsWaiting } from '../data/incidents.ts'
import { EXCEPTION_DAYS, EXCEPTION_RULE, EXCEPTION_TITLE_KEY, exceptionHint, triageProfile, useHomeSignals, type ExceptionKind, type ExceptionRow, type HomeSignals } from '../data/exceptions.ts'
import { localDate } from '../lib/time.ts'
import { DailyWeatherCard, useDailyDue } from './measures/Daily.tsx'
import { formatClock, sessionMs, sessionTitle, useMyWork, type RunningWork } from '../data/work.ts'
import type { ReviewItem } from '../data/workTime.ts'
import { Pill } from '../ui/components.tsx'
import { NoticeLine } from '../ui/NoticeLine.tsx'
import { useFirstReady, useWriteAccess } from '../ui/hooks.ts'
import { useNow } from '../ui/useNow.ts'
import { RowLink } from '../ui/RowLink.tsx'
import { CourseScene } from '../ui/Emblem.tsx'
import { Picto } from '../ui/pictos.tsx'
import { profileOf, type Profile } from '../ui/profile.ts'
import { LiveShareBanner } from '../ui/LiveShareBanner.tsx'
import { freshRows, onePerPerson, useLivePositions } from '../data/liveMap.ts'
import { useLocationOn } from '../data/location.ts'
import { useForecast, type ForecastRead } from '../data/weatherForecast.ts'
import { ForecastCard } from '../ui/ForecastCard.tsx'

/** Today (M19: one screen, an order per profile; M23 items 17, 30, 34: the jardinier's day leads with his jobs and "Commencer" on the first screen, the
 *  direction's with its exceptions, the mechanic's with his workshop's numbers). M16-A replaces the direction's and the mechanic's exception block with
 *  "À traiter" (docs/M16.md "A"): who is there today and what is on today (B1's notices) first, then every exception as a count and a link, red before
 *  amber, the thresholds printed as the rule; the order M23 decided stays (the exceptions, then the planning, then the rest). Everything here reads the
 *  device's own copy; the server reads (the service due, the review count, and A's three signals) are asked only of the profiles that show them, and say
 *  "hors ligne" rather than vanish. The notices in force today are a card for everyone (the jardinier reads a closed green too).
 *  M24-W: the forecast card (the next 24 hours, docs/M24.md "Screens") for whoever holds forecast.read — the Superintendent and the direction — and NEVER on the
 *  shared workstation, whoever is unlocked there; right after the daily reading card, wherever that card stands. Its one read of `api.forecast_view` also feeds
 *  "À traiter"'s stale line (the fifth server signal), so the page asks once. */

/** A timer that is open on this device: the clock runs on screen, whatever the network says. A session opened on a repair order (item 44) is named after it. */
function RunningCard({ w, title }: { w: RunningWork<PlanJob>; title: string }) {
  const { t } = useTranslation()
  const now = useNow()
  const tone = w.running
    ? '!border-emerald-600 !bg-emerald-50 text-emerald-950 dark:!border-emerald-500 dark:!bg-emerald-950/40 dark:text-emerald-100'
    : '!border-amber-500 !bg-amber-50 text-amber-950 dark:!border-amber-500 dark:!bg-amber-950/40 dark:text-amber-100'
  return (
    <Link to="/work/$sessionId" params={{ sessionId: w.session.id }} className={`card flex items-center justify-between gap-3 !border-2 ${tone}`} data-testid="today-timer" data-session={w.session.id} data-state={w.running ? 'running' : 'paused'}>
      <span className="flex min-w-0 flex-col">
        <span className="truncate text-lg font-semibold">{title}</span>
        <span className="text-sm">{w.running ? t('work.state.running') : t('work.state.paused')}</span>
      </span>
      <span className="font-mono text-3xl font-bold tabular-nums" data-testid="today-timer-clock">{formatClock(sessionMs(w.intervals, now))}</span>
    </Link>
  )
}

/** The register's count: the workshop's and the direction's first number. */
function RegisterRow() {
  const { t } = useTranslation()
  const { db } = useClub()
  const count = useLiveQuery(async () => {
    const [m, o] = await Promise.all([db.asset.count(), db.overlay.where('table').equals('fleet.asset').count()])
    return m + o
  }, [db], 0)
  return <RowLink to="/assets" icon="tractor" title={t('assets.title')} aside={<span className="text-3xl font-bold tabular-nums text-brand dark:text-fairway" data-testid="assets-count">{count}</span>} />
}

type Due = ReturnType<typeof useServiceDue>
const dueCountOf = (due: Due): number => (due.data ?? []).filter((r) => r.status === 'overdue' || r.status === 'due').length
/** The service due, from the server: always on the screen (M23 item 34), and "hors ligne" when the server cannot be asked rather than gone. */
function ServiceRow({ due, online }: { due: Due; online: boolean }) {
  const { t } = useTranslation()
  const n = dueCountOf(due)
  const offline = due.data === null && (!online || !due.online)
  return <RowLink to="/service" icon="wrench" tone={n > 0 ? 'sand' : 'slate'} testId="today-service" title={n > 0 ? t('today.serviceCount', { count: n }) : t('today.service')} hint={offline ? <span data-testid="today-service-offline">{t('today.serviceOffline')}</span> : t('today.serviceOpen')} />
}

/** For the direction: how many people are on a running task with a position shared now (online only: positions are never kept on a device). */
function OnCourseCard() {
  const { t } = useTranslation()
  const live = useLivePositions(true)
  const now = useNow(30_000)
  if (!live.online || live.rows === null) return null
  const n = onePerPerson(freshRows(live.rows, now)).length
  return <RowLink to="/map" icon="people" tone="water" testId="today-live" title={<span data-count={n}>{t('today.onCourse', { count: n })}</span>} hint={t('today.onCourseOpen')} />
}

/** M16-G: the way to "Signaler un incident" (docs/M16.md G), for whoever may report one, the shared workstation included; while reports wait on this device it
 *  says how many (from the local-only stubs: the text is never read here). */
function IncidentRow() {
  const { t } = useTranslation()
  const waiting = useIncidentsWaiting()
  return <RowLink to="/incidents/new" icon="firstaid" tone="flag" testId="today-incident" title={t('incidents.rowTitle')}
    hint={waiting > 0 ? <span data-testid="today-incident-waiting">{t('incidents.waiting', { count: waiting })}</span> : t('incidents.rowHint')} />
}

/** The greeting: one line on a phone (the date and the name; M23 items 17 and 30), the course scene only from `sm:`, and never for the jardinier. */
function Hero({ plain }: { plain: boolean }) {
  const { t, i18n } = useTranslation()
  const { org, site } = useClub()
  const date = new Intl.DateTimeFormat(i18n.language, { weekday: 'long', day: 'numeric', month: 'long', timeZone: site.timezone }).format(new Date())
  const line = (
    <>
      <span className="text-sm font-semibold uppercase tracking-wider text-brand-dark first-letter:uppercase dark:text-slate-300" data-testid="today-date">{date}</span>
      <h1 className="font-serif text-xl font-bold sm:text-2xl">{t('today.welcome', { name: org.display_name })}</h1>
    </>
  )
  if (plain) return <div className="flex min-h-14 flex-wrap items-center gap-x-3 gap-y-0.5 text-brand-dark dark:text-slate-50" data-testid="today-hero" data-plain="1">{line}</div>
  return (
    <div className="relative overflow-hidden rounded-2xl border border-fairway/40 bg-fairway-light text-brand-dark shadow-sm dark:border-night-line dark:bg-night-card dark:text-slate-50" data-testid="today-hero">
      <CourseScene className="absolute inset-x-0 bottom-0 hidden h-24 w-full opacity-90 sm:block" />
      <div className="relative flex flex-wrap items-center gap-x-3 gap-y-0.5 p-4 sm:flex-col sm:items-start sm:gap-1 sm:pb-24">{line}</div>
    </div>
  )
}

/** M16-A / B1: the notices in force today, one line each (a competition and its start, a green closed): the first card of everyone's day. */
function NoticesCard({ notices, areaById, lang }: { notices: NoticeRow[]; areaById: Map<string, AreaRow>; lang: string }) {
  if (notices.length === 0) return null
  return <div className="flex flex-col gap-1.5" data-testid="today-notices">{notices.map((n) => <NoticeLine key={n.id} notice={n} areaById={areaById} lang={lang} />)}</div>
}

const RED = '!border-red-500 !bg-red-50 text-red-950 dark:!border-red-600 dark:!bg-red-950/40 dark:text-red-100'
const KIND_ICON: Record<ExceptionKind, string> = { restriction: 'alert', unassigned: 'calendar', fault: 'sprinkler', service: 'wrench', defect: 'wrench', reading: 'measure', observation: 'leaf', qualification: 'badge', review: 'clipboard', device: 'phone', forecast: 'weather' }
/** One exception as a row: a count and a link, red or sand; the words are data/exceptions.ts's maps. `/observe` is N's page (routed at Z's close: the cast goes then). */
function ExceptionRowLink({ r, areaById, lang }: { r: ExceptionRow; areaById: Map<string, AreaRow>; lang: string }) {
  const { t } = useTranslation()
  return (
    <RowLink to={r.to as never} search={r.search} hash={r.hash} icon={KIND_ICON[r.kind]} tone={r.severity === 'red' ? 'flag' : 'sand'} testId={`today-exc-${r.kind}`} className={r.severity === 'red' ? RED : ''}
      title={<span data-count={r.count} data-severity={r.severity}>{t(EXCEPTION_TITLE_KEY[r.kind], { count: r.count, days: EXCEPTION_DAYS[r.kind] })}</span>} hint={exceptionHint(r, areaById, lang, t)} />
  )
}

/** The roster line: "13 présents · 3 absents" with the absent by name, or the team's size alone when the day's availability cannot be read. */
function RosterRow({ s }: { s: HomeSignals }) {
  const { t } = useTranslation()
  const r = s.roster
  if (!r) return null
  const title = r.present !== null ? `${t('today.roster.present', { count: r.present })} · ${t('today.roster.absent', { count: r.absent })}` : t('today.roster.team', { count: r.team })
  const hint = r.away.length > 0 ? `${t('plan.awayList')}: ${r.away.map((a) => (a.status === 'partial' ? `${a.name} (${t('plan.avail.partial')})` : a.name)).join(', ')}`
    : r.present !== null ? t('today.roster.nobodyAway') : t('today.roster.offline')
  return <RowLink to="/plan" search={{ v: 'week' }} icon="people" testId="today-roster" title={<span data-present={r.present ?? ''} data-absent={r.absent ?? ''} data-team={r.team}>{title}</span>} hint={<span data-testid="today-roster-hint">{hint}</span>} />
}

/** "À traiter": the roster line, the notices of the day, then the exceptions (red first), the lines put away on this device counted, the rule printed. The rows of
 *  three kinds (machines out, the review, the service due) are the page's own elements, so they keep their words, their ids and the mechanic's numbers. */
function TriageSection({ s, own, areaById, lang, notices, forecastRule }: { s: HomeSignals; own: Partial<Record<ExceptionKind, ReactNode>>; areaById: Map<string, AreaRow>; lang: string; notices: ReactNode; forecastRule: boolean }) {
  const { t } = useTranslation()
  return (
    <section className="flex flex-col gap-2" data-testid="today-triage" data-ready={s.ready ? '1' : '0'}>
      <h2 className="text-lg font-bold">{t('today.triage.title')}</h2>
      <RosterRow s={s} />
      {notices}
      {s.shown.map((r) => <div key={r.kind} data-testid="today-triage-row" data-kind={r.kind}>{own[r.kind] ?? <ExceptionRowLink r={r} areaById={areaById} lang={lang} />}</div>)}
      {s.ready && s.shown.length === 0 && <p className="meta" data-testid="today-triage-empty">{t('today.triage.empty')}</p>}
      {/* M16a review: the way back to a line put away is a 56-px row (M23's floor), not a text link inside a .meta line */}
      {s.hidden.length > 0 && <RowLink to="/review" search={{ tab: 'signals' }} icon="clipboard" tone="slate" testId="today-triage-hidden" title={t('today.triage.hiddenSeen', { count: s.hidden.length })} hint={t('today.triage.seeSignals')} />}
      <p className="meta" data-testid="today-triage-rule">{t('today.triage.rule', EXCEPTION_RULE)}</p>
      {/* M24-W: the forecast's line has its own rule (the connector's staleness), printed with the others for whoever reads the forecast */}
      {forecastRule && <p className="meta" data-testid="today-triage-forecast-rule">{t('today.triage.forecastRule')}</p>}
    </section>
  )
}

/** The direction's and the mechanic's day (M23 item 30, M16-A): "À traiter" first, then the planning, then the rest; the rows with nothing to say (no machine
 *  out, nothing to review, no service due) keep their place after the planning, and so does a row put away with "Vu" on this device.
 *  The weather card's place is decided once the notebook has answered and then held while the page is open: the reading is typed on that very card, and a
 *  card moved to the end the moment it was saved would be remounted by React (a new element at a new place) and lose its "Relevé enregistré" (the daily-weather
 *  e2e saw the banner vanish); the next visit puts the card last. The other exception rows are links with nothing to lose. */
function TriageDay({ profile, b, online, reviewQ, plan, areaById, lang, notices, fleetVisible, forecast }: {
  profile: Profile; b: Record<string, ReactNode>; online: boolean; reviewQ: readonly Pick<ReviewItem, 'id'>[] | null; plan: readonly PlanJob[]
  areaById: Map<string, AreaRow>; lang: string; notices: ReactNode; fleetVisible: boolean; forecast: ForecastRead | undefined
}) {
  const due = useServiceDue()
  const names = useNames()
  const s = useHomeSignals(profile, { jobs: plan, review: reviewQ, due: due.data, names: names ?? undefined, forecast })
  const weather = useDailyDue()
  const weatherMissing = useFirstReady(weather.due, weather.ready)
  const service = <ServiceRow due={due} online={online} />
  const own: Partial<Record<ExceptionKind, ReactNode>> = { restriction: b.atelier, review: b.review, service }
  const inSection = new Set(s.shown.map((r) => r.kind))
  const rest = (kind: ExceptionKind, el: ReactNode) => (inSection.has(kind) ? null : el)
  const triage = <TriageSection s={s} own={own} areaById={areaById} lang={lang} notices={notices} forecastRule={forecast !== undefined} />
  if (profile === 'mechanic') {
    return <>{triage}{fleetVisible && <RegisterRow />}{rest('restriction', b.atelier)}{fleetVisible && rest('service', service)}{b.jobs}{b.actions}{rest('review', b.review)}{b.terrain}{b.weather}{b.forecast}{b.map}</>
  }
  return (
    <>
      {triage}
      {weatherMissing === true && b.weather}
      {weatherMissing === true && b.forecast}
      {b.jobs}
      {b.onCourse}
      {b.actions}
      {b.terrain}
      {b.map}
      <RegisterRow />
      {rest('restriction', b.atelier)}
      {rest('service', service)}
      {rest('review', b.review)}
      {weatherMissing !== true && b.weather}
      {weatherMissing !== true && b.forecast}
    </>
  )
}

/** The workstation's fleet rows (the register, the service due): the server is asked for the due list here only. */
function FleetRows({ online }: { online: boolean }) {
  const due = useServiceDue()
  return <><RegisterRow /><ServiceRow due={due} online={online} /></>
}

const ORDER_OPEN = (o: WorkOrderRow) => OPEN_WORK_ORDER_STATES.includes(o.state)

export function TodayPage() {
  const { t, i18n } = useTranslation()
  const { org, site, personId, kiosk, can } = useClub()
  const st = useEngineState()
  const lang = i18n.language
  const profile: Profile = profileOf(site.roles, !!kiosk)
  const field = profile === 'field', mechanic = profile === 'mechanic'
  const assets = useAssets()
  const orders = useWorkOrders()
  const defects = useDefectViews()
  const { blockers, ready } = useAvailability()
  const outCount = (assets ?? []).filter((a) => a.lifecycle !== 'retired' && blockers(a.id).length > 0).length
  const today = localDate(new Date(), site.timezone)
  const me = kiosk?.session?.personId ?? personId
  const { ready: planReady, plan } = usePlan(lang)
  const areas = useAreas()
  const areaById = new Map((areas ?? []).map((a) => [a.id, a as AreaRow]))
  const assetById = new Map((assets ?? []).map((a) => [a.id, a as AssetRow]))
  const mine = me ? plan.ofPerson(me, today) : []
  const work = useMyWork(plan.jobs)
  const exec = useWriteAccess('job.execute')
  const review = useWriteAccess('review.read')
  const location = useLocationOn()                                   // M21: off at the site, nobody is "on the course" for the app
  const openOn = new Map(work.mine.filter((w) => w.session.job_id).map((w) => [w.session.job_id!, w]))
  // M23 item 23: before the first pull the board is not empty, it is not here yet
  const loadingPlan = st.lastSyncAt === null && st.phase === 'syncing'
  const online = st.online !== false && (typeof navigator === 'undefined' || navigator.onLine !== false)
  // M23 item 57 (with 40's guard): the fleet rows for whoever may read the register, and on the workstation only for an unlocked person who manages it
  const assetRead = can('asset.read')
  const fleetVisible = !field && assetRead && (profile !== 'kiosk' || can('asset.manage'))
  const reviewVisible = review.permitted && !field
  const reviewQ = useRpc<ReviewItem[]>('review_queue', { p_org: org.org_id, p_site: site.site_id }, reviewVisible)
  const boardCount = plan.day(today).length
  // M15-A: the fill-up is a fact the workstation may take under a PIN (rule 8), so its row is the one Terrain row a kiosk shows
  const refuel = can('stock.record')
  // M16-G: an incident is reported by whoever works, even with no signal, the workstation included (under the person's PIN: rule 9)
  const incident = can('incident.report')
  const terrain = (!kiosk && (can('water.record') || can('measurement.record'))) || refuel || incident
  // M16-B1: the notices in force today, for everyone
  const dayNotices = noticesFor(useNotices(), today)
  const notices = <NoticesCard notices={dayNotices} areaById={areaById} lang={lang} />
  // M24-W: the forecast, for forecast.read and never on the shared workstation (the shed PC never shows it, whoever is unlocked there)
  const forecastAllowed = can('forecast.read') && !kiosk
  const forecastRead = useForecast(forecastAllowed)

  const hero = <Hero plain={field} />
  const timers = work.mine.length > 0 && (
    <div className="flex flex-col gap-2" data-testid="today-timers">
      {work.mine.map((w) => <RunningCard key={w.session.id} w={w} title={sessionTitle(w.session, w.job?.title_text, orders, assets, t)} />)}
      <LiveShareBanner />
    </div>
  )
  // the workshop row: the jardinier sees it only when a machine is out (item 17); the mechanic reads his numbers on it (item 34)
  const defectsOpen = (defects ?? []).filter((v) => v.status === 'open').length
  const ordersOpen = (orders ?? []).filter(ORDER_OPEN).length
  const atelier = ready && assets && assetRead && (!field || outCount > 0) && (
    <RowLink to="/workshop" icon={outCount > 0 ? 'alert' : 'wrench'} tone={outCount > 0 ? 'flag' : 'slate'} testId="today-out"
      className={outCount > 0 ? RED : ''}
      title={outCount > 0 ? t('today.outCount', { count: outCount }) : t('workshop.title')}
      hint={mechanic
        ? <span data-testid="today-out-hint">{[...(outCount > 0 ? [] : [t('today.outShort', { count: outCount })]), t('today.defectsOpen', { count: defectsOpen }), t('today.ordersOpen', { count: ordersOpen })].join(' · ')}</span>
        : outCount > 0 ? t('today.outOpen') : t('today.workshop')} />
  )
  const reviewRow = reviewVisible && (
    <RowLink to="/review" icon="clipboard" tone="sand" testId="today-review" title={reviewQ.data ? <span data-count={reviewQ.data.length}>{t('today.reviewCount', { count: reviewQ.data.length })}</span> : t('work.reviewTitle')} hint={t('work.reviewOpen')} />
  )
  const onCourse = profile === 'direction' && can('job.execute') && location && <OnCourseCard />
  // item 19: the machine on its own line, then the areas summarised with the starting one ("9 greens · départ Green 1 · Rouge"); the full list is on the job's page
  const jobs = planReady && (
    <div className="flex flex-col gap-2" data-testid="today-jobs">
      {me && mine.length > 0 ? mine.map(({ job, assignment }) => {
        const start = assignment.start_area_id ? areaById.get(assignment.start_area_id) : undefined
        const where = [job.areaIds.length ? areasSummary(job.areaIds, areaById, lang, t) : null, start ? t('today.startAt', { area: areaShort(start, areaById, lang, t) }) : null].filter(Boolean).join(' · ')
        return (
          <div key={assignment.id} className="card flex flex-col gap-2" data-testid="today-job" data-job={job.id}>
            <Link to="/plan/job/$jobId" params={{ jobId: job.id }} className="flex min-h-14 flex-col justify-center gap-0.5">
              <span className="text-lg font-semibold">{job.title_text}{job.scheduled_start_local ? ` · ${timeShort(job.scheduled_start_local)}` : ''}</span>
              {assignment.asset_id && <span className="font-medium" data-testid="today-job-machine">{machineLabel(assetById.get(assignment.asset_id))}</span>}
              {where && <span className="meta" data-testid="today-job-areas">{where}</span>}
            </Link>
            <div className="flex flex-wrap items-center gap-2">
              {job.state !== 'planned' && <Pill tone={job.state === 'done' ? 'green' : 'amber'}>{t(`plan.state.${job.state}`)}</Pill>}
              {openOn.has(job.id)
                ? <Link to="/work/$sessionId" params={{ sessionId: openOn.get(job.id)!.session.id }} className="btn" data-testid="today-open-timer">{t('work.openTimer')}</Link>
                : job.state !== 'done' && exec.allowed && <Link to="/work/start" search={{ job: job.id, ...(assignment.asset_id ? { asset: assignment.asset_id } : {}) } as never} className="btn btn-primary" data-testid="today-start">{t('work.start')}</Link>}
            </div>
          </div>
        )
      }) : null}
      <RowLink to="/plan" icon="calendar" testId="today-plan"
        title={me && mine.length === 0 ? (loadingPlan ? <span data-testid="today-loading-plan">{t('today.loadingPlan')}</span> : profile === 'direction' && boardCount > 0 ? t('today.board', { count: boardCount }) : t('today.noJobs')) : t('today.board', { count: boardCount })}
        hint={t('today.boardOpen')} />
    </div>
  )
  const actions = exec.permitted && (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      <Link to="/work/start" className="btn" data-testid="today-start-other"><Picto name="clock" className="size-5" />{t('work.startOther')}</Link>
      <Link to="/time" className="btn" data-testid="today-time"><Picto name="history" className="size-5" />{t('work.myTime')}</Link>
    </div>
  )
  // item 46: the field facts (a run, a fault, a reading, M16-N's observation) from Today, not through Réglages; `/observe` is routed at Z's close (the cast goes then)
  const terrainRows = terrain && (
    <section className="flex flex-col gap-2" data-testid="today-field">
      <h2 className="text-lg font-bold">{t('today.field')}</h2>
      {!kiosk && can('water.record') && <RowLink to="/water" search={{ tab: 'runs' }} icon="sprinkler" tone="water" testId="today-run" title={t('today.recordRun')} />}
      {!kiosk && can('water.record') && <RowLink to="/water" search={{ tab: 'faults', new: '1' }} icon="alert" tone="flag" testId="today-fault" title={t('today.raiseFault')} />}
      {!kiosk && can('measurement.record') && <RowLink to="/measures" icon="measure" tone="sand" testId="today-measure" title={t('today.takeReading')} />}
      {!kiosk && can('measurement.record') && <RowLink to="/observe" icon="leaf" tone="green" testId="today-observe" title={t('observe.rowTitle')} hint={t('observe.rowHint')} />}
      {/* M15-A: "Faire le plein" (scan or pick the machine first) */}
      {refuel && <RowLink to="/refuel" icon="fuel" tone="sand" testId="today-refuel" title={t('stock.refuel.rowTitle')} hint={t('stock.refuel.rowHint')} />}
      {/* M16-G: "Signaler un incident" (offline; how many reports wait on this device, from their stubs, never the text) */}
      {incident && <IncidentRow />}
    </section>
  )
  const weather = can('measurement.record') && !kiosk && <DailyWeatherCard compact={field} />
  const forecast = forecastAllowed && <ForecastCard read={forecastRead} />
  const map = <RowLink to="/map" icon="map" tone="flag" testId="today-map" title={t('today.map')} hint={t('today.mapOpen')} />

  if (field) {
    return <section className="flex flex-col gap-4" data-profile={profile}>{hero}{timers}{notices}{jobs}{actions}{terrainRows}{weather}{forecast}{atelier}{map}</section>
  }
  if (triageProfile(profile)) {
    return (
      <section className="flex flex-col gap-4" data-profile={profile}>
        {hero}{timers}
        <TriageDay profile={profile} b={{ atelier, review: reviewRow, jobs, onCourse, actions, terrain: terrainRows, map, weather, forecast }} online={online} reviewQ={reviewQ.data} plan={plan.jobs} areaById={areaById} lang={lang} notices={notices} fleetVisible={fleetVisible} forecast={forecastAllowed ? forecastRead : undefined} />
      </section>
    )
  }
  return <section className="flex flex-col gap-4" data-profile={profile}>{hero}{timers}{notices}{atelier}{jobs}{actions}{reviewRow}{terrainRows}{weather}{forecast}{map}{fleetVisible && <FleetRows online={online} />}</section>
}
